import React, { useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import Typography from '@material-ui/core/Typography'
import FormControl from '@material-ui/core/FormControl'
import TextField from '@material-ui/core/TextField'
import MenuItem from '@material-ui/core/MenuItem'
import LinearProgress from '@material-ui/core/LinearProgress'
import RoomOutlinedIcon from 'components/material-symbol/icons/Room'
import AddOutlinedIcon from 'components/material-symbol/icons/Add'
import { isEmpty } from 'lodash'
import { useAuth } from 'lib/auth'
import { gtmEvent } from 'lib/gtm'
import SectionEmptyState from 'components/section-empty-state'
import { TonalButton } from 'components/empty-page-state'
import RecurringHoursSidebar from '../availability-tab/recurring-hours-sidebar'
import { formatFacilityLabel } from '../availability-tab/utils'
import Dialog from 'components/onboarding-dialog'
import useStyles from './availability-manager.styles'

const GET_AVAILABILITY = gql`
  query CoachAvailability {
    coachAvailabilities {
      coachFacility {
        id
        name
      }
      startDate
      timeZone
      timeSlots {
        day
        startTime
        endTime
      }
    }
  }
`

const ONBOARDING_ADD_FACILITY_PATH = '/pga-coach/onboarding/facilities/new'

const NoFacilitiesEmptyState = () => (
  <SectionEmptyState
    Icon={RoomOutlinedIcon}
    title="Add a facility first"
    caption="Before you set your working hours, add where you coach. Once a location is saved, you can define your general availability here."
    bordered={false}
    action={
      <TonalButton
        startIcon={<AddOutlinedIcon />}
        to={ONBOARDING_ADD_FACILITY_PATH}
      >
        Add Facility
      </TonalButton>
    }
  />
)

const AvailabilityDoneTip = ({ classes, onDone, disabled }) => (
  <Box className={classes.doneTipBanner}>
    <Typography component="div" className={classes.doneTipText}>
      <strong>Done adding your availability?</strong>
      <br />
      Head back to Onboarding when you&apos;re ready to continue.
    </Typography>
    <TonalButton onClick={onDone} disabled={disabled}>
      Return to Dashboard
    </TonalButton>
  </Box>
)

const AvailabilityManagerWrapper = ({ children, footer }) => (
  <>
    {children}
    {footer}
  </>
)

const FacilitySelector = ({
  availabilities,
  selectedAvailability,
  onChange,
  classes,
}) => (
  <FormControl
    variant="outlined"
    size="small"
    className={classes.facilityField}
  >
    {availabilities.length === 1 ? (
      <TextField
        disabled
        size="small"
        value={formatFacilityLabel(selectedAvailability)}
        variant="outlined"
      />
    ) : (
      <TextField
        select
        size="small"
        value={selectedAvailability.coachFacility.id}
        variant="outlined"
        label="Facility"
        onChange={e => onChange(e.target.value)}
      >
        {availabilities.map(availability => (
          <MenuItem
            key={availability.coachFacility.id}
            value={availability.coachFacility.id}
          >
            {formatFacilityLabel(availability)}
          </MenuItem>
        ))}
      </TextField>
    )}
  </FormControl>
)

const AvailabilityManager = ({
  refetchCoachOnboardingStatus,
  coachOnboardingProgress,
}) => {
  const classes = useStyles()
  const { user } = useAuth()
  const [selectedLocationId, setSelectedLocationId] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const { data, loading, refetch } = useQuery(GET_AVAILABILITY)

  const showDoneTip = coachOnboardingProgress?.facilityAdded
  const doneTipFooter = showDoneTip ? (
    <>
      <AvailabilityDoneTip
        classes={classes}
        onDone={() => setDialogOpen(true)}
        disabled={!coachOnboardingProgress?.availabilityAdded}
      />
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Nice work!"
        caption="Your working hours are all set. Head back to Onboarding to continue setting up your coaching profile."
        action
      />
    </>
  ) : null

  const handleSuccess = () => {
    if (user.onboarding) {
      gtmEvent({
        event: 'formSubmit',
        formCategory: 'bookings',
        formAction: 'edit-onboarding-availability',
        formStatus: 'success',
      })
    }
    refetch()
    refetchCoachOnboardingStatus && refetchCoachOnboardingStatus()
  }

  if (loading) {
    return (
      <AvailabilityManagerWrapper footer={doneTipFooter}>
        <Card variant="outlined" className={classes.loadingCard}>
          <Box p={2}>
            <LinearProgress />
          </Box>
        </Card>
      </AvailabilityManagerWrapper>
    )
  }

  if (isEmpty(data.coachAvailabilities)) {
    return <NoFacilitiesEmptyState />
  }

  const selectedAvailability = selectedLocationId
    ? data.coachAvailabilities.find(
      availability => availability.coachFacility.id === selectedLocationId,
    )
    : data.coachAvailabilities[0]

  return (
    <AvailabilityManagerWrapper footer={doneTipFooter}>
      <RecurringHoursSidebar
        availability={selectedAvailability}
        onSuccess={handleSuccess}
        facilityControl={
          <FacilitySelector
            availabilities={data.coachAvailabilities}
            selectedAvailability={selectedAvailability}
            onChange={setSelectedLocationId}
            classes={classes}
          />
        }
      />
    </AvailabilityManagerWrapper>
  )
}

export default AvailabilityManager
